import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { useTenant } from '../../context/TenantContext';
import { useAuth } from '../../context/AuthContext';
import { exportCsv, exportExcel, exportPdf } from '../../utils/export';


const expenseStatusLabel = (status) => {
  const map = {
    pending: 'pendiente',
    approved: 'aprobado',
    rejected: 'rechazado'
  };
  return map[status] || status || '';
};

const Expenses = () => {
  const { activeTenant, onboardingStatus } = useTenant();
  const { isOperator } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [createData, setCreateData] = useState({
    supplier_name: '',
    supplier_nif: '',
    invoice_number: '',
    expense_date: '',
    due_date: '',
    description: '',
    category: 'servicios',
    base_amount: '',
    tax_rate: 21,
    tax_amount: '',
    total_amount: '',
    payment_method: 'transferencia',
    is_paid: false,
    paid_date: '',
    notes: '',
  });
  const [editData, setEditData] = useState({
    supplier_name: '',
    supplier_nif: '',
    invoice_number: '',
    due_date: '',
    description: '',
    category: 'servicios',
    base_amount: '',
    tax_rate: 21,
    tax_amount: '',
    total_amount: '',
    payment_method: 'transferencia',
    is_paid: false,
    paid_date: '',
    notes: '',
    status: 'pending',
  });

  const loadExpenses = async () => {
    if (!activeTenant) return;
    try {
      const res = await api.get(`/native/expenses/${activeTenant}`);
      setExpenses(res.data);
    } catch (error) {
      console.error('Error loading expenses:', error);
      toast.error('Error al cargar gastos');
    }
  };

  useEffect(() => {
    loadExpenses();
  }, [activeTenant]);

  const isReadyToOperate = onboardingStatus?.is_ready_to_operate === true;
  const hasSubmittedOnboarding =
    onboardingStatus?.onboarding_status && onboardingStatus.onboarding_status !== 'DRAFT';
  const canOperate = isOperator() || isReadyToOperate;

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!activeTenant) return;
    if (!canOperate) {
      toast.error('Cuenta en revision');
      return;
    }
    try {
      await api.post('/native/expenses', {
        tenant_id: activeTenant,
        supplier_name: createData.supplier_name,
        supplier_nif: createData.supplier_nif || null,
        invoice_number: createData.invoice_number || null,
        expense_date: createData.expense_date,
        due_date: createData.due_date || null,
        description: createData.description,
        category: createData.category,
        base_amount: Number(createData.base_amount),
        tax_rate: Number(createData.tax_rate),
        tax_amount: Number(createData.tax_amount),
        total_amount: Number(createData.total_amount),
        payment_method: createData.payment_method,
        is_paid: createData.is_paid,
        paid_date: createData.is_paid ? createData.paid_date || null : null,
        notes: createData.notes || null,
      });
      toast.success('Gasto creado');
      setCreateOpen(false);
      setCreateData({
        supplier_name: '',
        supplier_nif: '',
        invoice_number: '',
        expense_date: '',
        due_date: '',
        description: '',
        category: 'servicios',
        base_amount: '',
        tax_rate: 21,
        tax_amount: '',
        total_amount: '',
        payment_method: 'transferencia',
        is_paid: false,
        paid_date: '',
        notes: '',
      });
      loadExpenses();
    } catch (error) {
      console.error('Error creating expense:', error);
      toast.error('Error al crear gasto');
    }
  };

  const openEdit = (expense) => {
    setSelectedExpense(expense);
    setEditData({
      supplier_name: expense.supplier_name || '',
      supplier_nif: expense.supplier_nif || '',
      invoice_number: expense.invoice_number || '',
      due_date: expense.due_date ? expense.due_date.slice(0, 10) : '',
      description: expense.description || '',
      category: expense.category || 'servicios',
      base_amount: expense.base_amount ?? '',
      tax_rate: expense.tax_rate ?? 21,
      tax_amount: expense.tax_amount ?? '',
      total_amount: expense.total_amount ?? '',
      payment_method: expense.payment_method || 'transferencia',
      is_paid: Boolean(expense.is_paid),
      paid_date: expense.paid_date ? expense.paid_date.slice(0, 10) : '',
      notes: expense.notes || '',
      status: expense.status || 'pending',
    });
    setEditOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedExpense || !activeTenant) return;
    if (!canOperate) {
      toast.error('Cuenta en revision');
      return;
    }
    try {
      await api.put(`/native/expenses/${activeTenant}/${selectedExpense.id}`, {
        supplier_name: editData.supplier_name,
        supplier_nif: editData.supplier_nif || null,
        invoice_number: editData.invoice_number || null,
        due_date: editData.due_date || null,
        description: editData.description,
        category: editData.category,
        base_amount: Number(editData.base_amount),
        tax_rate: Number(editData.tax_rate),
        tax_amount: Number(editData.tax_amount),
        total_amount: Number(editData.total_amount),
        payment_method: editData.payment_method,
        is_paid: editData.is_paid,
        paid_date: editData.is_paid ? editData.paid_date || null : null,
        notes: editData.notes || null,
        status: editData.status,
      });
      toast.success('Gasto actualizado');
      setEditOpen(false);
      setSelectedExpense(null);
      loadExpenses();
    } catch (error) {
      console.error('Error updating expense:', error);
      toast.error('Error al actualizar gasto');
    }
  };

  const handleDelete = async (expense) => {
    if (!activeTenant) return;
    if (!canOperate) {
      toast.error('Cuenta en revision');
      return;
    }
    const confirmed = window.confirm(`Eliminar gasto de ${expense.supplier_name}?`);
    if (!confirmed) return;
    try {
      await api.delete(`/native/expenses/${activeTenant}/${expense.id}`);
      toast.success('Gasto eliminado');
      loadExpenses();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Error al eliminar gasto');
    }
  };

  const expenseExportRows = expenses.map((exp) => ({
    date: exp.expense_date,
    supplier: exp.supplier_name,
    category: exp.category,
    total: exp.total_amount,
    paid: exp.is_paid ? 'si' : 'no',
    status: expenseStatusLabel(exp.status),
  }));

  return (
    <div data-testid="client-expenses-page" className="max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-4xl font-heading font-bold">Gastos</h1>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => exportCsv(expenseExportRows, 'gastos.csv')}>
            Exportar CSV
          </Button>
          <Button variant="outline" onClick={() => exportExcel(expenseExportRows, 'gastos.xlsx')}>
            Exportar Excel
          </Button>
          <Button variant="outline" onClick={() => exportPdf(expenseExportRows, 'gastos.pdf', 'Gastos')}>
            Exportar PDF
          </Button>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="create-expense-button" disabled={!canOperate}>
                <Plus className="w-4 h-4" />Nuevo Gasto
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle>Nuevo Gasto</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Proveedor</Label>
                    <Input
                      value={createData.supplier_name}
                      onChange={(e) => setCreateData({ ...createData, supplier_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>NIF</Label>
                    <Input
                      value={createData.supplier_nif}
                      onChange={(e) => setCreateData({ ...createData, supplier_nif: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Numero factura</Label>
                    <Input
                      value={createData.invoice_number}
                      onChange={(e) => setCreateData({ ...createData, invoice_number: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Fecha gasto</Label>
                    <Input
                      type="date"
                      value={createData.expense_date}
                      onChange={(e) => setCreateData({ ...createData, expense_date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Fecha vencimiento</Label>
                    <Input
                      type="date"
                      value={createData.due_date}
                      onChange={(e) => setCreateData({ ...createData, due_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Categoria</Label>
                    <Select
                      value={createData.category}
                      onValueChange={(value) => setCreateData({ ...createData, category: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="suministros">suministros</SelectItem>
                        <SelectItem value="alquiler">alquiler</SelectItem>
                        <SelectItem value="marketing">marketing</SelectItem>
                        <SelectItem value="servicios">servicios</SelectItem>
                        <SelectItem value="transporte">transporte</SelectItem>
                        <SelectItem value="otros">otros</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Descripcion</Label>
                  <Textarea
                    value={createData.description}
                    onChange={(e) => setCreateData({ ...createData, description: e.target.value })}
                    required
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Base</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={createData.base_amount}
                      onChange={(e) => setCreateData({ ...createData, base_amount: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>IVA %</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      value={createData.tax_rate}
                      onChange={(e) => setCreateData({ ...createData, tax_rate: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>IVA</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={createData.tax_amount}
                      onChange={(e) => setCreateData({ ...createData, tax_amount: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Total</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={createData.total_amount}
                      onChange={(e) => setCreateData({ ...createData, total_amount: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Metodo de pago</Label>
                    <Select
                      value={createData.payment_method}
                      onValueChange={(value) => setCreateData({ ...createData, payment_method: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="transferencia">transferencia</SelectItem>
                        <SelectItem value="tarjeta">tarjeta</SelectItem>
                        <SelectItem value="efectivo">efectivo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="paid"
                    type="checkbox"
                    checked={createData.is_paid}
                    onChange={(e) => setCreateData({ ...createData, is_paid: e.target.checked })}
                  />
                  <Label htmlFor="paid">Pagado</Label>
                  <Input
                    type="date"
                    className="w-44"
                    value={createData.paid_date}
                    onChange={(e) => setCreateData({ ...createData, paid_date: e.target.value })}
                    disabled={!createData.is_paid}
                  />
                </div>
                <div>
                  <Label>Notas</Label>
                  <Textarea
                    value={createData.notes}
                    onChange={(e) => setCreateData({ ...createData, notes: e.target.value })}
                  />
                </div>
                <Button type="submit" className="w-full">Crear gasto</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {!canOperate && !hasSubmittedOnboarding && (
        <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700">
          Completa la incorporacion para activar Gastos.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {expenses.map((exp) => (
          <Card key={exp.id}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm text-muted-foreground">{exp.expense_date}</div>
                  <div className="text-lg font-semibold">{exp.supplier_name}</div>
                  <div className="text-sm text-muted-foreground">{exp.category}</div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold">{exp.total_amount} EUR</div>
                  <div className="text-xs text-muted-foreground">{expenseStatusLabel(exp.status)}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => openEdit(exp)}>
                  <Pencil className="w-4 h-4 mr-1" />Editar
                </Button>
                <Button variant="destructive" size="sm" onClick={() => handleDelete(exp)}>
                  <Trash2 className="w-4 h-4 mr-1" />Eliminar
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Editar Gasto</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Proveedor</Label>
                <Input
                  value={editData.supplier_name}
                  onChange={(e) => setEditData({ ...editData, supplier_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>NIF</Label>
                <Input
                  value={editData.supplier_nif}
                  onChange={(e) => setEditData({ ...editData, supplier_nif: e.target.value })}
                />
              </div>
              <div>
                <Label>Numero factura</Label>
                <Input
                  value={editData.invoice_number}
                  onChange={(e) => setEditData({ ...editData, invoice_number: e.target.value })}
                />
              </div>
              <div>
                <Label>Fecha vencimiento</Label>
                <Input
                  type="date"
                  value={editData.due_date}
                  onChange={(e) => setEditData({ ...editData, due_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Categoria</Label>
                <Select
                  value={editData.category}
                  onValueChange={(value) => setEditData({ ...editData, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="suministros">suministros</SelectItem>
                    <SelectItem value="alquiler">alquiler</SelectItem>
                    <SelectItem value="marketing">marketing</SelectItem>
                    <SelectItem value="servicios">servicios</SelectItem>
                    <SelectItem value="transporte">transporte</SelectItem>
                    <SelectItem value="otros">otros</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Estado</Label>
                <Select
                  value={editData.status}
                  onValueChange={(value) => setEditData({ ...editData, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">pendiente</SelectItem>
                    <SelectItem value="approved">aprobado</SelectItem>
                    <SelectItem value="rejected">rechazado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Descripcion</Label>
              <Textarea
                value={editData.description}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Base</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editData.base_amount}
                  onChange={(e) => setEditData({ ...editData, base_amount: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>IVA %</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.1"
                  value={editData.tax_rate}
                  onChange={(e) => setEditData({ ...editData, tax_rate: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>IVA</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editData.tax_amount}
                  onChange={(e) => setEditData({ ...editData, tax_amount: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Total</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editData.total_amount}
                  onChange={(e) => setEditData({ ...editData, total_amount: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Metodo de pago</Label>
                <Select
                  value={editData.payment_method}
                  onValueChange={(value) => setEditData({ ...editData, payment_method: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transferencia">transferencia</SelectItem>
                    <SelectItem value="tarjeta">tarjeta</SelectItem>
                    <SelectItem value="efectivo">efectivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                id="paid-edit"
                type="checkbox"
                checked={editData.is_paid}
                onChange={(e) => setEditData({ ...editData, is_paid: e.target.checked })}
              />
              <Label htmlFor="paid-edit">Pagado</Label>
              <Input
                type="date"
                className="w-44"
                value={editData.paid_date}
                onChange={(e) => setEditData({ ...editData, paid_date: e.target.value })}
                disabled={!editData.is_paid}
              />
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea
                value={editData.notes}
                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
              />
            </div>
            <Button type="submit" className="w-full">Guardar cambios</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Expenses;
